import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, TrendingDown, Activity, Users,
  Calendar, Clock, AlertTriangle, CheckCircle, Target,
  RefreshCw, Brain, LineChart, PieChart, Lightbulb,
  ArrowUp, ArrowDown, Minus, Heart, Shield, DollarSign
} from 'lucide-react';
import {
  LineChart as RechartsLine,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0D9488', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#10B981'];

const AdvancedAnalytics = () => {
  const [insights, setInsights] = useState(null);
  const [trends, setTrends] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [riskScores, setRiskScores] = useState([]);
  const [benchmarks, setBenchmarks] = useState(null);
  const [visitAnalytics, setVisitAnalytics] = useState(null);
  const [productivity, setProductivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('insights');
  const [trendMetric, setTrendMetric] = useState('census');
  const [trendPeriod, setTrendPeriod] = useState('30d');

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    if (activeTab === 'trends') {
      loadTrends();
    }
  }, [trendMetric, trendPeriod]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadInsights(),
        loadTrends(),
        loadForecast(),
        loadRiskScores(),
        loadBenchmarks(),
        loadVisitAnalytics(),
        loadProductivity()
      ]);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
    setLoading(false);
  };

  const loadInsights = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/analytics/insights`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInsights(data);
      }
    } catch (error) {
      console.error('Error loading insights:', error);
    }
  };

  const loadTrends = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/phase27/analytics/trends/${trendMetric}?period=${trendPeriod}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setTrends(data);
      }
    } catch (error) {
      console.error('Error loading trends:', error);
    }
  };

  const loadForecast = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/analytics/forecast/census`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForecast(data);
      }
    } catch (error) {
      console.error('Error loading forecast:', error);
    }
  };

  const loadRiskScores = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/analytics/risk/patients`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRiskScores(data.patients || []);
      }
    } catch (error) {
      console.error('Error loading risk scores:', error);
    }
  };

  const loadBenchmarks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/analytics/benchmarks`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBenchmarks(data);
      }
    } catch (error) {
      console.error('Error loading benchmarks:', error);
    }
  };

  const loadVisitAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/analytics/visits`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVisitAnalytics(data);
      }
    } catch (error) {
      console.error('Error loading visit analytics:', error);
    }
  };

  const loadProductivity = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/analytics/productivity`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProductivity(data.staff || []);
      }
    } catch (error) {
      console.error('Error loading productivity:', error);
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'positive': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'critical': return <AlertTriangle className="h-5 w-5 text-red-500" />;
      default: return <Lightbulb className="h-5 w-5 text-blue-500" />;
    }
  };

  const getTrendIcon = (direction) => {
    if (direction === 'up') return <ArrowUp className="h-4 w-4 text-green-500" />;
    if (direction === 'down') return <ArrowDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-500" />;
  };

  const getRiskBadge = (level) => {
    const styles = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return <Badge className={styles[level]}>{level.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="advanced-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="h-7 w-7 text-teal-600" />
            Advanced Analytics
          </h1>
          <p className="text-gray-500">AI-powered insights and predictive metrics</p>
        </div>
        <Button onClick={loadAllData} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Key Metrics Summary */}
      {insights?.metrics_summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Current Census</p>
                  <p className="text-2xl font-bold">{insights.metrics_summary.current_census}</p>
                </div>
                <Users className="h-8 w-8 text-teal-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Quality Score</p>
                  <p className="text-2xl font-bold">{insights.metrics_summary.quality_score}%</p>
                </div>
                <Target className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Staff Utilization</p>
                  <p className="text-2xl font-bold">{insights.metrics_summary.staff_utilization}%</p>
                </div>
                <Activity className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Days in A/R</p>
                  <p className="text-2xl font-bold">{insights.metrics_summary.days_in_ar}</p>
                </div>
                <DollarSign className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="insights">AI Insights</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="forecast">Forecast</TabsTrigger>
          <TabsTrigger value="risk">Risk Scores</TabsTrigger>
          <TabsTrigger value="benchmarks">Benchmarks</TabsTrigger>
          <TabsTrigger value="productivity">Productivity</TabsTrigger>
        </TabsList>

        {/* AI Insights Tab */}
        <TabsContent value="insights" className="mt-4">
          <div className="space-y-4">
            {insights?.insights?.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Lightbulb className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No insights available</p>
                </CardContent>
              </Card>
            ) : (
              insights?.insights?.map((insight, idx) => (
                <Card key={idx} data-testid={`insight-${idx}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {getInsightIcon(insight.type)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">{insight.title}</h3>
                          <Badge variant="outline">{insight.category}</Badge>
                        </div>
                        <p className="text-gray-600 mb-2">{insight.message}</p>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-gray-700">Recommendation:</p>
                          <p className="text-sm text-gray-600">{insight.recommendation}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <CardTitle>Trend Analysis</CardTitle>
                <div className="flex gap-2">
                  <Select value={trendMetric} onValueChange={setTrendMetric}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="census">Census</SelectItem>
                      <SelectItem value="visits">Visits</SelectItem>
                      <SelectItem value="admissions">Admissions</SelectItem>
                      <SelectItem value="discharges">Discharges</SelectItem>
                      <SelectItem value="satisfaction">Satisfaction</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={trendPeriod} onValueChange={setTrendPeriod}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7d">7 Days</SelectItem>
                      <SelectItem value="30d">30 Days</SelectItem>
                      <SelectItem value="90d">90 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {trends?.data && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Current</p>
                      <p className="text-xl font-bold">{trends.statistics.current}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Average</p>
                      <p className="text-xl font-bold">{trends.statistics.average}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Change</p>
                      <p className="text-xl font-bold flex items-center justify-center gap-1">
                        {getTrendIcon(trends.statistics.trend_direction)}
                        {trends.statistics.change_percent}%
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Range</p>
                      <p className="text-xl font-bold">{trends.statistics.min} - {trends.statistics.max}</p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsLine data={trends.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="value" stroke="#0D9488" strokeWidth={2} />
                    </RechartsLine>
                  </ResponsiveContainer>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Forecast Tab */}
        <TabsContent value="forecast" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Census Forecast (30 Days)</CardTitle>
              <CardDescription>
                Predicted census with {forecast?.accuracy_historical}% historical accuracy
              </CardDescription>
            </CardHeader>
            <CardContent>
              {forecast?.forecast && (
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsLine data={forecast.forecast.slice(0, 14)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis domain={['dataMin - 5', 'dataMax + 5']} />
                    <Tooltip />
                    <Line type="monotone" dataKey="predicted_census" stroke="#0D9488" strokeWidth={2} name="Predicted" />
                    <Line type="monotone" dataKey="range_low" stroke="#E5E7EB" strokeDasharray="5 5" name="Low Range" />
                    <Line type="monotone" dataKey="range_high" stroke="#E5E7EB" strokeDasharray="5 5" name="High Range" />
                  </RechartsLine>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Risk Scores Tab */}
        <TabsContent value="risk" className="mt-4">
          <div className="space-y-4">
            {riskScores.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No risk data available</p>
                </CardContent>
              </Card>
            ) : (
              riskScores.map((patient, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                          <Heart className="h-6 w-6 text-gray-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{patient.patient_name}</h3>
                            {getRiskBadge(patient.risk_level)}
                          </div>
                          <p className="text-sm text-gray-500">
                            Risk Score: {patient.risk_score}/100
                          </p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {patient.risk_factors.map((factor, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {factor}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-700">Recommended Action:</p>
                        <p className="text-sm text-teal-600">{patient.recommended_action}</p>
                      </div>
                    </div>
                    <Progress value={patient.risk_score} className="mt-3" />
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Benchmarks Tab */}
        <TabsContent value="benchmarks" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Industry Benchmark Comparison</CardTitle>
              <CardDescription>
                Overall Percentile: {benchmarks?.overall_percentile}th
              </CardDescription>
            </CardHeader>
            <CardContent>
              {benchmarks?.metrics && (
                <div className="space-y-4">
                  {benchmarks.metrics.map((metric, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{metric.metric}</p>
                        <p className="text-sm text-gray-500">Benchmark: {metric.benchmark}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold">{metric.our_value}</p>
                        <div className="flex items-center gap-1 text-sm">
                          {metric.difference > 0 ? (
                            <span className="text-green-600">+{metric.difference}</span>
                          ) : (
                            <span className="text-red-600">{metric.difference}</span>
                          )}
                          <Badge variant="outline">{metric.percentile}th %ile</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Productivity Tab */}
        <TabsContent value="productivity" className="mt-4">
          <div className="space-y-4">
            {productivity.map((staff, idx) => (
              <Card key={idx}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-medium">{staff.staff_name}</h3>
                      <p className="text-sm text-gray-500 capitalize">{staff.role.replace('_', ' ')}</p>
                    </div>
                    <Badge className={staff.productivity_percent >= 100 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {staff.productivity_percent}%
                    </Badge>
                  </div>
                  <Progress value={Math.min(staff.productivity_percent, 100)} className="mb-3" />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Visits</p>
                      <p className="font-medium">{staff.visits_completed} / {staff.visits_target}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Avg Duration</p>
                      <p className="font-medium">{staff.avg_visit_duration} min</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Documentation</p>
                      <p className="font-medium">{staff.documentation_compliance}%</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Patient Rating</p>
                      <p className="font-medium">{staff.patient_satisfaction}/5.0</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdvancedAnalytics;
